import type { Device, Weather } from '../../api/apiTypes'
import { Lap } from '../../domain/lap/Lap'
import type { LapBounds } from '../../domain/lap/LapBounds'
import type { LapMetadata } from '../../domain/lap/LapMetadata'
import type { GpsPoint } from '../../domain/telemetry/processed/GpsPoint'
import type { ProcessedTelemetryData } from '../../domain/telemetry/processed/ProcessedTelemetryData'
import type { TelemetryDiagnostics } from '../../domain/telemetry/processed/TelemetryDiagnostics'
import type { TelemetryStatistics } from '../../domain/telemetry/processed/TelemetryStatistics'
import type {
  DeviceMetadata,
  RawTelemetryData,
} from '../../domain/telemetry/raw/RawTelemetryData'
import { PROCESSING_VERSION } from '../processingVersion'
import { readLayout, writeLayout } from './binaryLayout'
import { decodeTable, encodeTable, type TableLayout } from './columnTable'
import { ColumnWriter } from './columnWriter'
import { LAP_TABLE_FIELDS, type LapTableName } from './lapTables'
import { LapsFormatError } from './lapsFormatError'
import {
  isLapUnreliableReason,
  type LapUnreliableReason,
} from '../../telemetryChecks/lapChecks/lapReliability'

interface EncodedLap {
  metadata: LapMetadata
  bounds: LapBounds
  statistics: TelemetryStatistics
  diagnostics?: TelemetryDiagnostics
  device: Device
  weather: Weather
  sessionName: string
  topSpeed: number
  raw: { device?: DeviceMetadata; recordingStartTime?: Date }
  breakingPoints: number[]
  slowestPoints: number[]
  hasGyroscope: boolean
  hasAccelerometer: boolean
  unreliableReasons: LapUnreliableReason[]
  tables: Partial<Record<LapTableName, TableLayout>>
}

interface LapsHeader {
  version: number
  laps: EncodedLap[]
}

const STORED_LAP_FIELDS = [
  'id',
  'lapNumber',
  'driverName',
  'sessionId',
  'trackId',
  'vehicleId',
  'vehicleName',
  'bounds',
  'processedTelemetryData',
  'rawTelemetry',
  'spatialBuckets',
  'sessionName',
  'device',
  'hasGyroscope',
  'hasAccelerometer',
  'unreliableReasons',
  'weather',
  'breakingPoints',
  'slowestPoints',
  'topSpeed',
]

const LAP_FIELDS_THE_CONSTRUCTOR_DERIVES = [
  'lapTime',
  'indexedGps',
  'lapGroupingName',
  'lapStartDistance',
  'lapFinishDistance',
]

const LAP_READINGS = ['gyro', 'accl']

const LAP_FIELDS_OF_UI_STATE = ['isSelected', 'isShared']

const LAP_FIELDS = new Set([
  ...STORED_LAP_FIELDS,
  ...LAP_FIELDS_THE_CONSTRUCTOR_DERIVES,
  ...LAP_READINGS,
  ...LAP_FIELDS_OF_UI_STATE,
])

const PROCESSED_FIELD_FLAGS: Record<keyof ProcessedTelemetryData, true> = {
  gpsPoints: true,
  leanAngle: true,
  acceleration: true,
  gForce: true,
  statistics: true,
  diagnostics: true,
}

const RAW_FIELDS_STORED_OR_READINGS: Record<
  keyof RawTelemetryData,
  'stored' | 'reading'
> = {
  gps: 'reading',
  gyroscope: 'reading',
  accelerometer: 'reading',
  device: 'stored',
  recordingStartTime: 'stored',
}

const PROCESSED_FIELDS = new Set(Object.keys(PROCESSED_FIELD_FLAGS))
const RAW_FIELDS = new Set(Object.keys(RAW_FIELDS_STORED_OR_READINGS))

export function encodeLaps(laps: readonly Lap[]): Uint8Array {
  const writer = new ColumnWriter()
  const header: LapsHeader = {
    version: PROCESSING_VERSION.major,
    laps: laps.map((lap) => encodeLap(lap, writer)),
  }
  return writeLayout(header, writer.bytes())
}

export function decodeLaps(bytes: Uint8Array): Lap[] {
  const { header, data } = readLayout(bytes)
  const { laps } = lapsHeader(header)
  try {
    return laps.map((encoded) => decodeLap(encoded, data))
  } catch (error) {
    if (error instanceof LapsFormatError) throw error
    throw new LapsFormatError(`a lap could not be decoded: ${String(error)}`)
  }
}

function lapsHeader(header: unknown): LapsHeader {
  const { version, laps } = (header ?? {}) as Partial<LapsHeader>
  if (version !== PROCESSING_VERSION.major) {
    throw new LapsFormatError(
      `laps of processing major ${version}, expected ${PROCESSING_VERSION.major}`
    )
  }
  if (!Array.isArray(laps)) {
    throw new LapsFormatError('the header lists no laps')
  }
  return { version, laps }
}

function encodeLap(lap: Lap, writer: ColumnWriter): EncodedLap {
  assertOnlyKnownFields('Lap', lap, LAP_FIELDS)
  const processed = lap.processedTelemetryData
  const raw = lap.rawTelemetry
  assertOnlyKnownFields('processedTelemetryData', processed, PROCESSED_FIELDS)
  assertOnlyKnownFields('rawTelemetry', raw, RAW_FIELDS)
  const table = (name: LapTableName, rows: readonly object[] | undefined) =>
    rows ? encodeTable(name, rows, LAP_TABLE_FIELDS[name], writer) : undefined
  return {
    metadata: {
      lapId: lap.id,
      lapNumber: lap.lapNumber,
      driverName: lap.driverName,
      trackSessionId: lap.sessionId,
      trackId: lap.trackId,
      vehicleId: lap.vehicleId,
      vehicleName: lap.vehicleName,
    },
    bounds: lap.bounds,
    statistics: processed.statistics,
    diagnostics: processed.diagnostics,
    device: lap.device,
    weather: lap.weather,
    sessionName: lap.sessionName,
    topSpeed: lap.topSpeed,
    raw: { device: raw.device, recordingStartTime: raw.recordingStartTime },
    breakingPoints: indexesIn(
      processed.gpsPoints,
      lap.breakingPoints,
      'breakingPoints'
    ),
    slowestPoints: indexesIn(
      processed.gpsPoints,
      lap.slowestPoints,
      'slowestPoints'
    ),
    hasGyroscope: lap.hasGyroscope,
    hasAccelerometer: lap.hasAccelerometer,
    unreliableReasons: [...lap.unreliableReasons],
    tables: {
      gpsPoints: table('gpsPoints', processed.gpsPoints),
      leanAngle: table('leanAngle', processed.leanAngle),
      acceleration: table('acceleration', processed.acceleration),
      gForce: table('gForce', processed.gForce),
      spatialBuckets: table('spatialBuckets', lap.spatialBuckets),
    },
  }
}

function decodeLap(encoded: EncodedLap, data: ArrayBuffer): Lap {
  if (
    typeof encoded.hasGyroscope !== 'boolean' ||
    typeof encoded.hasAccelerometer !== 'boolean'
  ) {
    throw new LapsFormatError('a lap does not say which sensors it had')
  }
  if (
    !Array.isArray(encoded.unreliableReasons) ||
    !encoded.unreliableReasons.every(isLapUnreliableReason)
  ) {
    throw new LapsFormatError('a lap does not say whether it can be trusted')
  }
  const table = <T>(name: LapTableName): T[] | undefined => {
    const layout = encoded.tables[name]
    return layout
      ? (decodeTable(layout, LAP_TABLE_FIELDS[name], data) as T[])
      : undefined
  }
  const gpsPoints = table<GpsPoint>('gpsPoints') ?? []
  const processed: ProcessedTelemetryData = {
    gpsPoints,
    leanAngle: table('leanAngle') ?? [],
    acceleration: table('acceleration') ?? [],
    gForce: table('gForce') ?? [],
    statistics: encoded.statistics,
    diagnostics: encoded.diagnostics,
  }
  const rawTelemetry: RawTelemetryData = {
    gps: [],
    device: encoded.raw.device,
    recordingStartTime: encoded.raw.recordingStartTime,
  }
  const lap = new Lap(
    encoded.metadata,
    encoded.bounds,
    processed,
    rawTelemetry,
    {
      device: encoded.device,
      sessionName: encoded.sessionName,
      weather: encoded.weather,
      hasGyroscope: encoded.hasGyroscope,
      hasAccelerometer: encoded.hasAccelerometer,
      unreliableReasons: encoded.unreliableReasons,
      spatialBuckets: table('spatialBuckets') ?? [],
    }
  )
  lap.breakingPoints = encoded.breakingPoints.map((i) => gpsPoints[i])
  lap.slowestPoints = encoded.slowestPoints.map((i) => gpsPoints[i])
  lap.topSpeed = encoded.topSpeed
  return lap
}

function assertOnlyKnownFields(
  name: string,
  value: object,
  known: ReadonlySet<string>
): void {
  for (const key of Object.keys(value)) {
    if (!known.has(key)) {
      throw new LapsFormatError(
        `${name}.${key} has no place in the laps format`
      )
    }
  }
}

function indexesIn<T>(
  source: readonly T[],
  chosen: readonly T[],
  name: string
) {
  const positions = new Map<T, number>()
  source.forEach((entry, index) => {
    if (!positions.has(entry)) positions.set(entry, index)
  })
  return chosen.map((entry) => {
    const index = positions.get(entry)
    if (index === undefined) {
      throw new LapsFormatError(`${name} holds an entry its source does not`)
    }
    return index
  })
}
